

























































































































import { Component, Prop, Vue, Watch } from 'vue-property-decorator';

import Card from '@/components/UI/Card.vue';
import MmStates from '../../components/MmStates.vue';
import { createCancelSource } from '../../api/mmHttp';
import { fetchMonitoringResultsByIds } from '../../api/results';
import { fetchTask } from '../../api/tasks';
import { fetchTaskItems } from '../../api/taskItems';
import { buildPageQuery, createPaginationState, resolvePagerTotal } from '../../api/pagination';
import { formatDiagnostics, isCanceled, MmError, toMmError } from '../../core/errors';
import { resolveTaskMonitoringLevel } from '../../core/monitoringLevel';
import { getSessionStore, retryMobileSession } from '../../core/session';
import { MmTask } from '../../types/domain';
import { MmErrorCode, MmMonitoringLevel, MmTaskType } from '../../types/enums';
import { MmMonitoringBatch, resolvePositionsTotal } from '../results/batch';
import TaskCardHeader from './TaskCardHeader.vue';
import TaskItemsTable from './TaskItemsTable.vue';
import TaskResultsTable from './TaskResultsTable.vue';
import {
	hasExtension,
	MM_TASK_CARD_EXTENSIONS,
	MmTaskCardHeaderActionsPayload,
	MmTaskResultsContext,
} from './taskCardExtensions';
import {
	hasPlanTab,
	listVisibleTaskDetailTabs,
	MmTaskDetailTab,
	MmTaskDetailTabKey,
	normalizeTabKey,
	resolveDefaultTab,
} from './taskDetailTabs';
import { getTaskTypeProfile, MmTaskTypeProfile } from './taskTypeProfile';

const SECTION_HINT = 'tasks/detail';

@Component({
	components: { Card, MmStates, TaskCardHeader, TaskItemsTable, TaskResultsTable },
})
export default class TaskCard extends Vue {
	@Prop({ required: true }) readonly taskId!: string;

	@Prop({ default: null }) readonly tradingId!: number | null;

	/** Проп объявлен, хотя не используется: иначе он осел бы в $attrs и уехал атрибутом в DOM */
	@Prop({ default: '' }) readonly tradingName!: string;

	/** ant-design-vue 1.7.8 не размонтирует открытую вкладку, поэтому запросы идут только при isActive */
	@Prop({ default: false }) readonly isActive!: boolean;

	@Prop({ default: '' }) readonly initialTab!: string;

	@Prop({ default: null }) readonly prevTaskId!: string | null;

	@Prop({ default: null }) readonly nextTaskId!: string | null;

	@Prop({ default: null }) readonly batchSize!: number | null;

	@Prop({ default: null }) readonly monitoringBatch!: MmMonitoringBatch | null;

	@Prop({ default: null }) readonly resultsContext!: MmTaskResultsContext | null;

	@Prop({ default: null }) readonly highlightResultId!: string | null;

	@Prop({ default: null }) readonly highlightClassificationId!: string | null;

	task: MmTask | null = null;
	isLoading: boolean = true;
	loadError: MmError | null = null;

	planPositionsTotal: number | null = null;

	activeTab: MmTaskDetailTabKey = 'summary';

	private requestedTab: string = '';

	mountedTabs: { [key: string]: boolean } = {};

	private needsReload: boolean = false;

	private taskSource: any = null;

	private countsSource: any = null;

	get session() {
		return getSessionStore();
	}

	created(): void {
		this.requestedTab = this.initialTab ? String(this.initialTab) : '';
	}

	mounted(): void {
		this.loadTask();
	}

	beforeDestroy(): void {
		this.cancelActiveRequest();
	}

	@Watch('session.readyTick')
	onSessionReady(): void {
		if (!this.isActive) {
			this.needsReload = true;

			return;
		}

		this.loadTask();
	}

	@Watch('isActive')
	onActiveChanged(next: boolean): void {
		if (next === true && this.needsReload) {
			this.needsReload = false;
			this.loadTask();
		}
	}

	/** Вкладки и выбор сбрасываем: перенесённый выбор позиций ушёл бы не к тому заданию */
	@Watch('taskId')
	onTaskIdChanged(): void {
		this.resetTabsState();
		this.task = null;
		this.planPositionsTotal = null;
		this.loadError = null;
		this.requestedTab = '';
		this.loadTask();
	}

	get viewState(): string {
		if (this.loadError !== null) {
			if (this.loadError.code === MmErrorCode.NOT_FOUND) {
				return 'notFound';
			}

			if (this.loadError.code === MmErrorCode.FORBIDDEN) {
				return 'forbidden';
			}

			return 'error';
		}

		return this.task === null ? 'loading' : 'ready';
	}

	get errorDiagnostics(): string {
		return this.loadError === null ? '' : formatDiagnostics(this.loadError.diagnostics);
	}

	get isErrorRetryable(): boolean {
		return this.loadError !== null && this.loadError.retryable;
	}

	get taskType(): MmTaskType | null {
		return this.task !== null && this.task.type ? this.task.type : null;
	}

	get taskHasPlan(): boolean {
		return this.task !== null && hasPlanTab(this.task.type);
	}

	get profile(): MmTaskTypeProfile | null {
		return getTaskTypeProfile(this.taskType);
	}

	get monitoringLevel(): MmMonitoringLevel | null {
		if (this.task === null) {
			return null;
		}

		const classifications = this.task.classifications ? this.task.classifications : [];

		return resolveTaskMonitoringLevel(this.task.monitoringLevel, classifications);
	}

	get visibleTabs(): MmTaskDetailTab[] {
		return listVisibleTaskDetailTabs(this.taskType, this.profile);
	}

	isTabMounted(key: string): boolean {
		return this.mountedTabs[key] === true;
	}

	handleTabChange(key: string): void {
		this.requestedTab = key;
		this.applyTab(key);
		this.$emit('tab-change', this.activeTab);
	}

	private applyTab(raw: string): void {
		const key = normalizeTabKey(raw === '' ? null : raw, this.taskType);

		this.activeTab = key;
		this.$set(this.mountedTabs, key, true);
	}

	private resetTabsState(): void {
		this.mountedTabs = {};
		this.activeTab = resolveDefaultTab(null);
	}

	hasHeaderActions(): boolean {
		return hasExtension(this, MM_TASK_CARD_EXTENSIONS.headerActions);
	}

	get headerActionsPayload(): MmTaskCardHeaderActionsPayload {
		return {
			taskId: this.task !== null ? this.task.id : null,
			batchId: this.task !== null ? this.task.batchId : null,
			task: this.task,
			taskType: this.taskType,
			monitoringLevel: this.monitoringLevel,
			tradingNetworkId: this.tradingId,
			reload: this.reload,
			taskStatus: this.task !== null ? this.task.status : null,
		};
	}

	get canPrev(): boolean {
		return this.prevTaskId !== null && this.prevTaskId !== undefined && this.prevTaskId !== '';
	}

	get canNext(): boolean {
		return this.nextTaskId !== null && this.nextTaskId !== undefined && this.nextTaskId !== '';
	}

	handleBack(): void {
		this.$emit('back');
	}

	handlePrev(): void {
		if (this.canPrev) {
			this.$emit('navigate', this.prevTaskId);
		}
	}

	handleNext(): void {
		if (this.canNext) {
			this.$emit('navigate', this.nextTaskId);
		}
	}

	handleGoToBatch(payload: { batchId: string | null; taskId: string | null }): void {
		const batchId = payload && payload.batchId ? payload.batchId : null;
		const taskId = payload && payload.taskId ? payload.taskId : this.taskId;

		if (
			(batchId !== null && batchId !== undefined && String(batchId) !== '') ||
			(taskId !== null && taskId !== undefined && String(taskId) !== '')
		) {
			this.$emit('go-to-batch', { batchId, taskId });
		}
	}

	reload(): void {
		this.loadTask();
	}

	private loadTask(): void {
		if (!this.taskId) {
			return;
		}

		this.cancelActiveRequest();

		const source = createCancelSource();

		this.taskSource = source;
		this.isLoading = true;
		this.loadError = null;
		this.needsReload = false;

		fetchTask(this.taskId, this.tradingId, { cancelToken: source.token })
			.then((response) => {
				if (this.taskSource !== source) {
					return;
				}

				this.task = response ? response.data : null;
				this.planPositionsTotal = null;

				this.applyTab(this.requestedTab);
				this.refreshSummaryCounts();
			})
			.catch((error) => {
				if (isCanceled(error) || this.taskSource !== source) {
					return;
				}

				const mmError = toMmError(error, SECTION_HINT);

				this.task = null;
				this.planPositionsTotal = null;
				this.loadError = mmError;

				if (mmError.code === MmErrorCode.UNAUTHORIZED) {
					retryMobileSession().catch(() => undefined);
				}
			})
			.finally(() => {
				if (this.taskSource !== source) {
					return;
				}

				this.taskSource = null;
				this.isLoading = false;
			});
	}

	private cancelActiveRequest(): void {
		if (this.taskSource !== null) {
			this.taskSource.cancel();
			this.taskSource = null;
		}

		this.cancelCountsRequest();
	}

	private cancelCountsRequest(): void {
		if (this.countsSource !== null) {
			this.countsSource.cancel();
			this.countsSource = null;
		}
	}

	handleItemsTotal(total: number): void {
		if (typeof total === 'number' && !isNaN(total)) {
			this.planPositionsTotal = total;
		}
	}

	handleResultsTotal(total: number): void {
		if (this.task === null || hasPlanTab(this.task.type)) {
			return;
		}

		this.applyLoadedResultsCount(total);
	}

	private refreshSummaryCounts(): void {
		if (this.task === null || !this.task.id) {
			return;
		}

		this.cancelCountsRequest();

		const source = createCancelSource();
		const taskId = this.task.id;

		this.countsSource = source;

		if (hasPlanTab(this.task.type)) {
			const pager = createPaginationState(1);

			fetchTaskItems(taskId, buildPageQuery(pager), { cancelToken: source.token })
				.then((page) => {
					if (this.countsSource !== source) {
						return;
					}

					this.planPositionsTotal = page ? resolvePagerTotal(page, pager) : 0;
				})
				.catch((error) => {
					if (isCanceled(error) || this.countsSource !== source) {
						return;
					}

					const fallback = this.task !== null ? this.task.positionsTotal : null;

					this.planPositionsTotal = typeof fallback === 'number' ? fallback : null;
				})
				.finally(() => {
					if (this.countsSource === source) {
						this.countsSource = null;
					}
				});

			return;
		}

		fetchMonitoringResultsByIds(null, [taskId], { cancelToken: source.token })
			.then((rows) => {
				if (this.countsSource !== source) {
					return;
				}

				this.applyLoadedResultsCount(rows ? rows.length : 0);
			})
			.catch((error) => {
				if (isCanceled(error) || this.countsSource !== source) {
					return;
				}
			})
			.finally(() => {
				if (this.countsSource === source) {
					this.countsSource = null;
				}
			});
	}

	private applyLoadedResultsCount(loadedCount: number): void {
		if (this.task === null) {
			return;
		}

		const nextTotal = resolvePositionsTotal(this.task, loadedCount);

		if (nextTotal !== this.task.positionsTotal) {
			this.task = Object.assign({}, this.task, { positionsTotal: nextTotal });
		}
	}
}
